<template>
  <div :key="`scenario-menu-${scenarioId}`" ref="main">
    <item-header type="scenario" :choosable="true" :selected-id="scenarioId" @select="selectScenario" @close="$emit('close')">
      <template #content>
        <editable-item-title :item="scenario" />
      </template>
    </item-header>

    <drawer v-model:open="drawers.scenarioGeneral" :title="$t('labels.general')">
      <div class="column gap-4">
        <input-text
          ref="titleField"
          :value="scenario.title"
          :label="$t('labels.title')"
          @enter="descriptionField?.focus?.()"
          @update:value="updateKey('title', $event)"
        />

        <input-texteditor
          ref="descriptionField"
          :label="$t('labels.description')"
          :value="scenario.description"
          @update:value="updateKey('description', $event)"
        />

        <round-duration-chooser
          v-model:round-unit="scenarioRoundUnit"
          v-model:round-length="scenarioRoundLength"
          v-model:round-amount="scenarioRoundAmount"
          v-model:duration-type="scenarioDurationType"
          v-model:time-start="scenarioTimeStart"
          v-model:time-end="scenarioTimeEnd"
          v-model:round-customized="scenarioRoundCustomized"
          :round-name="simulationRoundName ?? undefined"
          :null-choosable="simulationRoundUnit != scenarioRoundUnit"
        />
      </div>
    </drawer>

    <drawer
      v-if="features.resource || features.target"
      v-model:open="drawers.scenarioStartStates"
      :title="$t('words.startValue', 3)"
    >
      <div class="column gap-4">
        <div v-if="features.resource">
          <label class="label">{{ $t("labels.resourceStartValues") }}</label>
          <button-list
            :items="resources"
            line-class="line_value"
            prop="title"
            :order="resourcesOrder"
          >
            <template #icons="{ item: resource }">
              <div
                v-if="scenario.resourceValues[resource.id] != null"
                class="button-icon button-item__icon button-item__icon-darker"
                @click.stop="deleteResourceValue(resource.id)"
              >
                <lucide-icon name="refresh-ccw" />
              </div>
            </template>
            <template #after="{ item }">
              <input-number
                :placeholder="item.value"
                :allow-null="true"
                :value="scenario.resourceValues[item.id]"
                :min="item.minValue"
                :max="item.maxValue"
                step="any"
                @update:value="setResourceValue(item.id, $event)"
              />
            </template>
          </button-list>
          <p v-if="resourcesSize === 0" class="message">
            {{
              $t("messages.createXsAndAddThem", [$t("objectNames.resource", 3)])
            }}
          </p>
        </div>

        <div v-if="features.target">
          <label class="label">{{ $t("labels.targetStartValues") }}</label>
          <button-list
            :items="targets"
            line-class="line_value"
            :order="targetsOrder"
            prop="title"
          >
            <template #icons="{ item }">
              <div
                v-if="scenario.targetValues[item.id] != null"
                class="button-icon button-item__icon button-item__icon-darker"
                @click="deleteTargetValue(item.id)"
              >
                <lucide-icon name="refresh-ccw" />
              </div>
            </template>
            <template #after="{ item }">
              <input-number
                :placeholder="item.value"
                :allow-null="true"
                :value="scenario.targetValues[item.id]"
                :min="item.minValue"
                :max="item.maxValue"
                step="any"
                @update:value="setTargetValue(item.id, $event)"
              />
            </template>
          </button-list>
          <p v-if="targetsSize === 0" class="message">
            {{
              $t("messages.createXsAndAddThem", [$t("objectNames.target", 3)])
            }}
          </p>
        </div>
      </div>
    </drawer>

    <drawer
      v-if="features.incident"
      v-model:open="drawers.scenarioIncidents"
      :title="$t('objectNames.incident', 3)"
    >
      <input-checkbox
        :label="$t('labels.autoAddIncidents')"
        :value="scenario.autoAddIncidents"
        @update:value="updateKey('autoAddIncidents', $event)"
      />
      <button-list
        class="mt-2"
        :items="connectedIncidents"
        :order="incidentsOrder"
        :removable="true"
        prop="title"
        @remove="disconnectIncident($event)"
      >
        <template #after-list>
          <input-dropdown
            v-if="unconnectedIncidentsSize > 0"
            ref="incidentselect"
            :options="unconnectedIncidents"
            :order="incidentsOrder"
            prop="title"
            @select="connectIncident"
          />
        </template>
      </button-list>
      <p v-if="incidentsSize === 0" class="message">
        {{ $t("messages.createXsAndAddThem", [$t("objectNames.incident", 3)]) }}
      </p>
    </drawer>

    <drawer
      v-if="features.action"
      v-model:open="drawers.scenarioActions"
      :title="$t('objectNames.action', 3)"
    >
      <input-checkbox
        :label="$t('labels.autoAddActions')"
        :value="scenario.autoAddActions"
        @update:value="updateKey('autoAddActions', $event)"
      />
      <button-list
        class="mt-2"
        :items="connectedActions"
        :order="actionsOrder"
        :removable="true"
        prop="title"
        @remove="disconnectAction($event)"
      >
        <template #after-list>
          <input-dropdown
            v-if="unconnectedActionsSize > 0"
            ref="actionselect"
            :options="unconnectedActions"
            :order="actionsOrder"
            prop="title"
            @select="connectAction"
          />
        </template>
      </button-list>
      <p v-if="actionsSize === 0" class="message">
        {{ $t("messages.createXsAndAddThem", [$t("objectNames.action", 3)]) }}
      </p>
    </drawer>

    <drawer v-model:open="drawers.scenarioEnds" :title="$t('labels.endCriteria')">
      <!-- eslint-disable prettier/prettier -->
      <button-list
        :items="ends"
        :order="ends.map((e) => e.id)"
        :sortable="true"
        :selected-item="(subSelection as ScenarioEndItem)"
        @select="openEnd($event)"
        @update:order="updateEndsOrder($event)"
      >
        <!-- eslint-enable prettier/prettier -->
        <template #default="{ item: end }">
          {{ end.title }}
        </template>

        <template #icons="{ item: end }">
          <button
            type="button"
            class="button-icon input__icon"
            @click="deleteEnd($event, end)"
          >
            <lucide-icon name="trash-2" />
          </button>
        </template>

        <template #after-list>
          <button class="button" type="button" @click="createEnd($event)">
            {{ $t("labels.add") }}
          </button>
        </template>
      </button-list>
    </drawer>

    <drawer v-model:open="drawers.scenarioStart" :title="$t('labels.startScreen')">
      <div class="column gap-4">
        <input-switch
          :value="scenario.showStartMessage"
          :label="$t('labels.showStartMessage')"
          @update:value="updateKey('showStartMessage', $event)"
        />
        <input-text
          :value="scenario.startTitle ?? undefined"
          :label="$t('labels.title')"
          @update:value="updateKey('startTitle', $event)"
        />
        <input-texteditor
          :value="scenario.startText ?? undefined"
          :label="$t('labels.description')"
          @update:value="updateKey('startText', $event)"
        />
        <input-image
          :id="scenario.startImageId ?? undefined"
          :control-object-fit="true"
          :compression="0.9"
          :max-width="1000"
          :max-height="1000"
          @update="setStartImage"
          @delete="deleteStartImage"
        />
      </div>
    </drawer>

    <drawer v-model:open="drawers.scenarioEnd" :title="$t('labels.endScreen')">
      <div class="column gap-4">
        <input-text
          :value="scenario.endTitle ?? undefined"
          :label="$t('labels.title')"
          @update:value="updateKey('endTitle', $event)"
        />
        <input-texteditor
          :value="scenario.endText ?? undefined"
          :label="$t('labels.description')"
          @update:value="updateKey('endText', $event)"
        />
        <input-image
          :id="scenario.endImageId ?? undefined"
          :compression="0.9"
          :max-width="1000"
          :max-height="1000"
          :control-object-fit="true"
          @update="setEndImage"
          @delete="deleteEndImage"
        />
      </div>
    </drawer>

    <drawer
      v-model:open="drawers.scenarioVisibility"
      :title="$t('labels.visibility')"
    >
      <h3 class="label mt-2">{{ $t("headings.impulseVisibility") }}</h3>
      <div class="column gap-1 mt-1">
        <input-checkbox
          :value="scenario.impulsesFromImpactsVisible"
          :label="$t('objectNames.impact', 3)"
          @update:value="updateKey('impulsesFromImpactsVisible', $event)"
        />
        <input-checkbox
          :value="scenario.impulsesFromFlowsVisible"
          :label="$t('objectNames.flow', 3)"
          @update:value="updateKey('impulsesFromFlowsVisible', $event)"
        />
        <input-checkbox
          :value="scenario.impulsesFromMomentumsVisible"
          :label="$t('objectNames.momentum', 3)"
          @update:value="updateKey('impulsesFromMomentumsVisible', $event)"
        />
        <input-checkbox
          :value="scenario.impulsesFromEventsVisible"
          :label="$t('objectNames.incident', 3)"
          @update:value="updateKey('impulsesFromEventsVisible', $event)"
        />
        <input-checkbox
          :value="scenario.impulsesFromActionsVisible"
          :label="$t('objectNames.action', 3)"
          @update:value="updateKey('impulsesFromActionsVisible', $event)"
        />
      </div>

      <h3 class="label mt-2">{{ $t("labels.effectVisibility") }}</h3>
      <div class="column gap-1 mt-1">
        <input-radio
          :value="scenario.effectsVisibility"
          :data="EFFECTS_VISIBILITY.asIs"
          :label="$t('labels.effectAsIsParent')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="scenario.effectsVisibility"
          :data="EFFECTS_VISIBILITY.visible"
          :label="$t('labels.effectForceVisible')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="scenario.effectsVisibility"
          :data="EFFECTS_VISIBILITY.valuesHidden"
          :label="$t('labels.effectValuesHidden')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="scenario.effectsVisibility"
          :data="EFFECTS_VISIBILITY.hidden"
          :label="$t('labels.effectForceHidden')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
      </div>
    </drawer>

    <menu-group v-if="scenariosSize > 1">
      <button
        class="button button_secondary mt-2"
        type="button"
        @click="deleteScenario"
      >
        {{ $t("actions.deleteScenario", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick, useTemplateRef } from "vue";
import { pickBy } from "lodash-es";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputImage from "@editor/items/image/InputImage.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import InputRadio from "@core/input/RadioButton.vue";

import ButtonList from "@core/ui/ButtonList.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import Drawer from "@core/ui/DrawerBlock.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";

import RoundDurationChooser from "@editor/items/simulationRound/RoundDurationChooser.vue";

import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useScenarioEndStore } from "@editor/items/scenarioEnd/scenarioEnds.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useFeatures } from "@editor/store/features.store";

import type { Handleable } from "@core/types/Events";
import type { ScenarioEndItem } from "../scenarioEnd/ScenarioEndItem";
import type { ScenarioItem } from "./ScenarioItem";
import type { RoundUnit } from "../simulationRound/SimulationRoundItem";

const props = defineProps<{
  scenarioId: number;
}>();

const emit = defineEmits<{
  (e: "select", item: ScenarioEndItem): void;
  (e: "select-main", item: ScenarioItem): void;
  (e: "deselect", item: ScenarioEndItem): void;
  (e: "click", event: MouseEvent): void;
  (e: "delete"): void;
  (e: "close"): void;
}>();

const features = useFeatures();

const incidentselect = ref<null | InstanceType<typeof InputDropdown>>(null);
const actionselect = ref<null | InstanceType<typeof InputDropdown>>(null);
const descriptionField = ref<null | InstanceType<typeof InputTexteditor>>(
  null
);
const titleField = useTemplateRef("titleField");

const drawers = useDrawerStates();

const EFFECTS_VISIBILITY = {
  asIs: "as-is",
  hidden: "hidden",
  visible: "visible",
  valuesHidden: "values-hidden"
} as const;

const scenarioStore = useScenarioStore();
const settings = useSettingsStore();

const scenario = computed(() => scenarioStore.items[props.scenarioId]);

const targets = computed(() => useTargetStore().items);
const incidents = computed(() => useIncidentStore().items);
const actions = computed(() => useActionStore().items);
const resources = computed(() => useResourceStore().items);

const targetsOrder = computed(() => useTargetStore().order);
const incidentsOrder = computed(() => useIncidentStore().order);
const actionsOrder = computed(() => useActionStore().order);
const resourcesOrder = computed(() => useResourceStore().order);

const targetsSize = computed(() => useTargetStore().length);
const incidentsSize = computed(() => useIncidentStore().length);
const actionsSize = computed(() => useActionStore().length);
const resourcesSize = computed(() => useResourceStore().length);
const scenariosSize = computed(() => scenarioStore.length);

const connectedIncidents = computed(() => {
  return pickBy(incidents.value, (incident) =>
    scenario.value.incidentIds.includes(incident.id)
  );
});
const unconnectedIncidents = computed(() => {
  return pickBy(
    incidents.value,
    (incident) => !scenario.value.incidentIds.includes(incident.id)
  );
});
const unconnectedIncidentsSize = computed(() => {
  return Object.keys(unconnectedIncidents.value).length;
});

const connectedActions = computed(() => {
  return pickBy(actions.value, (action) =>
    scenario.value.actionIds.includes(action.id)
  );
});
const unconnectedActions = computed(() => {
  return pickBy(
    actions.value,
    (action) => !scenario.value.actionIds.includes(action.id)
  );
});
const unconnectedActionsSize = computed(() => {
  return Object.keys(unconnectedActions.value).length;
});

const ends = computed(() => {
  return useScenarioEndStore().byScenario[props.scenarioId] || [];
});

const simulationRoundUnit = computed(() => {
  return settings.simulationRoundUnit;
});

const scenarioRoundAmount = computed( {
  get() {
    return (
      scenario.value.maxRounds || settings.simulationRoundAmount
    );
  },
  set(value: number | "" | null) {
    if (value == 0 || value == "") value = null;
    updateKey("maxRounds", value);
  }
});
const scenarioRoundUnit = computed( {
  get() {
    return (
      scenario.value.roundUnit || settings.simulationRoundUnit
    );
  },
  set(value: RoundUnit | null) {
    updateKey("roundUnit", value);
  }
});
const scenarioRoundLength = computed({
  get() {
    return (
      scenario.value.roundLength || settings.simulationRoundLength
    );
  },
  set(value: number | "" | null) {
    if (value == 0 || value == "") value = null;
    updateKey("roundLength", value);
  }
});
const scenarioDurationType = computed({
  get() {
    return (
      scenario.value.durationType ||
      settings.simulationDurationType
    );
  },
  set(value: "roundAmount" | "timeframe" | 0 | "" | null) {
    if (value == 0 || value == "") value = null;
    updateKey("durationType", value);
  }
});
const scenarioTimeStart = computed({
  get() {
    return (
      (scenario.value.timeStart || settings.simulationTimeStart) ??
      undefined
    );
  },
  set(value: 0 | string | "" | null) {
    if (value == 0 || value == "") value = null;
    if (value != null) value = String(value);
    updateKey("timeStart", value);
  }
});
const scenarioTimeEnd = computed({
  get() {
    return (
      (scenario.value.timeEnd || settings.simulationTimeEnd) ??
      undefined
    );
  },
  set(value: string | number | null) {
    if (value == 0 || value == "") value = null;
    if (value != null) value = String(value);
    updateKey("timeEnd", value);
  }
});
const scenarioRoundCustomized = computed({
  get() {
    return (
      scenario.value.roundCustomized ||
      settings.simulationRoundCustomized
    );
  },
  set(value: boolean | null | number | "") {
    if (value == 0 || value == "") value = null;
    if (value != null) value = !!value;
    updateKey("roundCustomized", value);
  }
});

const subSelection = computed(() => {
  return useVariablesViewStore().subSelection;
});

const simulationRoundName = computed(() => {
  return settings.simulationRoundName;
});

const mounted = ref(false);
watch(() => props.scenarioId, () => {
  if (mounted.value) {
    focusTitleIfEmpty();
  }
}, {
  immediate: true,
});

onMounted(() => {
  focusTitleIfEmpty();
  mounted.value = true;
})


function connectIncident({ item }: { item: { id: string } }) {
  let iId = parseInt(item.id);
  scenarioStore.connectIncident({
    id: props.scenarioId,
    incidentId: iId
  });
  incidentselect.value?.clear?.();
}
function disconnectIncident(incident: { id: number }) {
  scenarioStore.disconnectIncident({
    id: props.scenarioId,
    incidentId: incident.id
  });
}

function connectAction({ item }: { item: { id: string } }) {
  let iId = parseInt(item.id);
  scenarioStore.connectAction({
    id: props.scenarioId,
    actionId: iId
  });
  actionselect.value?.clear?.();
}
function disconnectAction(action: { id: number }) {
  scenarioStore.disconnectAction({
    id: props.scenarioId,
    actionId: action.id
  });
}

function setTargetValue(targetId: number, value: number) {
  scenarioStore.setTargetValue({
    id: props.scenarioId,
    targetId,
    value
  });
}
function deleteTargetValue(targetId: number) {
  scenarioStore.deleteTargetValue({
    id: props.scenarioId,
    targetId
  });
}

function setResourceValue(resourceId: number, value: number) {
  scenarioStore.setResourceValue({
    id: props.scenarioId,
    resourceId,
    value
  });
}
function deleteResourceValue(resourceId: number) {
  scenarioStore.deleteResourceValue({
    id: props.scenarioId,
    resourceId
  });
}

function openEnd(end: ScenarioEndItem, mouseEvent: Handleable | null = null) {
  if (mouseEvent && mouseEvent.hasBeenHandled) return;
  emit("select", end);
  if (mouseEvent) mouseEvent.hasBeenHandled = true;
}
function closeEnd(end: ScenarioEndItem) {
  emit("deselect", end);
}

async function createEnd(event: MouseEvent & Handleable) {
  event.preventDefault();
  console.log("create scenario end");

  const end = await useScenarioEndStore().create({
    scenarioId: props.scenarioId
  });
  openEnd(end, event);
}
function deleteEnd(mouseEvent: MouseEvent & Handleable, end: ScenarioEndItem) {
  closeEnd(end);
  mouseEvent.hasBeenHandled = true;
  const scenarioEndStore = useScenarioEndStore();

  scenarioEndStore.delete({
    id: end.id
  });
}
function updateEndsOrder(order: number[]) {
  useScenarioEndStore().saveOrder(order);
}

function focusTitleIfEmpty() {
  if (scenario.value.title.length === 0) {
    drawers.scenarioGeneral = true;
    nextTick(() => {
      titleField.value?.focus();
    });
  }
}

function selectScenario(scenarioId: number | null) {
  if (scenarioId === props.scenarioId) {
    return;
  }
  if (scenarioId !== null) {
    emit("select-main", scenarioStore.items[scenarioId]);
  }
}

function updateKey<K extends keyof ScenarioItem>(key: K, value: ScenarioItem[K]) {
  scenarioStore.updateKey(props.scenarioId, key, value);
}
function deleteScenario() {
  emit("delete");
  scenarioStore.delete({
    id: props.scenarioId
  });
}
function setStartImage(image: { id: number }) {
  updateKey("startImageId", image.id);
}
function deleteStartImage() {
  updateKey("startImageId", null);
}
function setEndImage(image: { id: number }) {
  updateKey("endImageId", image.id);
}
function deleteEndImage() {
  updateKey("endImageId", null);
}
</script>
